<template>
  <div class="page-shell">
    <CCard class="border-0 shadow-sm page-card">
      <CCardHeader class="d-flex flex-wrap justify-content-between align-items-center page-header">
        <div class="mr-3 mb-2 mb-md-0">
          <div class="text-uppercase small eyebrow">Operations</div>
          <strong class="h4 d-block mb-1">Stock Updates</strong>
          <div class="small text-muted">Ledger and item stock overview with quick entry</div>
        </div>
      </CCardHeader>

      <CCardBody>
        <CAlert v-if="errorMessage" color="danger" show class="mb-3">{{ errorMessage }}</CAlert>
        <CAlert v-if="successMessage" color="success" show class="mb-3">{{ successMessage }}</CAlert>

        <CRow>
          <CCol lg="7" class="mb-3 mb-lg-0">
            <CCard class="panel-card border-0 h-100">
              <CCardHeader class="panel-header">Add Stock Entry</CCardHeader>
              <CCardBody>
                <CRow>
                  <CCol md="6" class="mb-3">
                    <label class="small mb-1 d-block text-muted">Product</label>
                    <select class="form-control" v-model="form.item_id">
                      <option disabled value="">Select Product</option>
                      <option v-for="item in items" :key="item.id" :value="String(item.id)">
                        {{ item.name }}
                      </option>
                    </select>
                  </CCol>
                  <CCol md="6" class="mb-3">
                    <CInput label="Account" v-model.trim="form.account" />
                  </CCol>
                  <CCol md="12" class="mb-3">
                    <CInput label="Narration" v-model.trim="form.narration" />
                  </CCol>
                  <CCol md="6" class="mb-3">
                    <label class="small mb-1 d-block text-muted">Transaction Type</label>
                    <select class="form-control" v-model="form.movementType">
                      <option value="debit">Add Stock (Debit)</option>
                      <option value="credit">Reduce Stock (Credit)</option>
                    </select>
                  </CCol>
                  <CCol md="6" class="mb-3">
                    <CInput label="Quantity" type="number" min="0" v-model.number="form.quantity" />
                  </CCol>
                </CRow>
                <CButton color="primary" :disabled="isSaving" @click="saveEntry">
                  {{ isSaving ? 'Saving...' : 'Add Stock' }}
                </CButton>
              </CCardBody>
            </CCard>
          </CCol>

          <CCol lg="5">
            <CCard class="panel-card border-0 h-100">
              <CCardHeader class="panel-header d-flex justify-content-between align-items-center">
                <span>Items Inventory</span>
                <CButton color="secondary" size="sm" variant="outline" :disabled="isLoadingItems" @click="loadItems">
                  Refresh
                </CButton>
              </CCardHeader>
              <CCardBody>
                <div v-if="isLoadingItems" class="text-center py-4 text-muted">Loading items...</div>
                <div v-else class="table-responsive table-wrap">
                  <table class="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Name</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!items.length">
                        <td colspan="3" class="text-center text-muted">No items</td>
                      </tr>
                      <tr v-for="item in items" :key="item.id">
                        <td>{{ item.id }}</td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.quantity }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <CCard class="panel-card border-0 mt-3">
          <CCardHeader class="panel-header d-flex justify-content-between align-items-center">
            <span>Stock Ledger</span>
            <CButton color="secondary" size="sm" variant="outline" :disabled="isLoadingLedger" @click="loadLedger(ledger.currentPage)">
              Refresh
            </CButton>
          </CCardHeader>
          <CCardBody>
            <div v-if="isLoadingLedger" class="text-center py-4 text-muted">Loading ledger...</div>
            <div v-else class="table-responsive table-wrap">
              <table class="table table-sm mb-0 table-hover">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Item Id</th>
                    <th>Narration</th>
                    <th>Account</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th>User Id</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!ledger.rows.length">
                    <td colspan="7" class="text-center text-muted">No items</td>
                  </tr>
                  <tr v-for="row in ledger.rows" :key="row.id">
                    <td>{{ row.id }}</td>
                    <td>{{ row.item_id }}</td>
                    <td class="wide-cell">{{ row.narration }}</td>
                    <td>{{ row.account }}</td>
                    <td>{{ row.debit }}</td>
                    <td>{{ row.credit }}</td>
                    <td>{{ row.user_id }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex justify-content-end align-items-center mt-3 pager-wrap">
              <CButton size="sm" class="mr-2" variant="outline" color="primary" :disabled="ledger.currentPage <= 1 || isLoadingLedger" @click="loadLedger(ledger.currentPage - 1)">
                Prev
              </CButton>
              <span class="small text-muted">Page {{ ledger.currentPage }} of {{ ledger.lastPage }}</span>
              <CButton size="sm" class="ml-2" variant="outline" color="primary" :disabled="ledger.currentPage >= ledger.lastPage || isLoadingLedger" @click="loadLedger(ledger.currentPage + 1)">
                Next
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'StockUpdates',
  data () {
    return {
      form: {
        item_id: '',
        narration: '',
        account: '',
        movementType: 'debit',
        quantity: null
      },
      items: [],
      ledger: {
        rows: [],
        currentPage: 1,
        lastPage: 1
      },
      isLoadingItems: false,
      isLoadingLedger: false,
      isSaving: false,
      errorMessage: '',
      successMessage: ''
    }
  },
  created () {
    this.loadItems()
    this.loadLedger(1)
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    loadItems () {
      this.isLoadingItems = true
      axios.get(`${this.$apiAdress}/api/items?token=${this.token()}`)
        .then((response) => {
          this.items = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load items list.')
        })
        .finally(() => {
          this.isLoadingItems = false
        })
    },
    loadLedger (page = 1) {
      this.isLoadingLedger = true
      axios.get(`${this.$apiAdress}/api/stock?page=${page}&token=${this.token()}`)
        .then((response) => {
          const data = response.data || {}
          this.ledger.rows = Array.isArray(data.data) ? data.data : []
          this.ledger.currentPage = Number(data.current_page || 1)
          this.ledger.lastPage = Number(data.last_page || 1)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load stock ledger.')
        })
        .finally(() => {
          this.isLoadingLedger = false
        })
    },
    saveEntry () {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.form.item_id || !this.form.account || !this.form.narration || !this.form.quantity || Number(this.form.quantity) <= 0) {
        this.errorMessage = 'Product, narration, account and valid quantity are required.'
        return
      }

      const payload = {
        item_id: Number(this.form.item_id),
        account: this.form.account,
        narration: this.form.narration
      }

      if (this.form.movementType === 'credit') {
        payload.credit = Number(this.form.quantity)
      } else {
        payload.debit = Number(this.form.quantity)
      }

      this.isSaving = true
      axios.post(`${this.$apiAdress}/api/stock?token=${this.token()}`, payload)
        .then(() => {
          this.successMessage = 'Stock entry added successfully.'
          this.form.narration = ''
          this.form.account = ''
          this.form.quantity = null
          this.loadItems()
          this.loadLedger(1)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to add stock entry.')
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }
}
</script>

<style scoped>
.page-shell {
  background: linear-gradient(180deg, #f5f8fd 0%, #eef3fa 100%);
}

.page-card {
  border-radius: 16px;
}

.page-header {
  background: linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%);
}

.eyebrow {
  letter-spacing: 0.08em;
  color: #66748a;
  font-weight: 700;
}

.panel-card {
  border-radius: 14px;
  background: #fff;
}

.panel-header {
  background: #f8fbff;
  color: #4a5872;
  font-weight: 700;
}

.table-wrap {
  border: 1px solid #e7edf5;
  border-radius: 12px;
  overflow: hidden;
}

.table-wrap thead th {
  background: #f8fbff;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.74rem;
  white-space: nowrap;
}

.wide-cell {
  min-width: 220px;
  white-space: normal;
}

.pager-wrap {
  gap: 0.5rem;
}
</style>
